import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, MessageSquare, MessageSquarePlus, Send } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { Conversa, ConversaResumo } from '../../api/tipos';
import { Chat } from '../../componentes/Chat';
import { Pagina, TituloPagina } from '../../componentes/Pagina';
import {
  AreaTexto,
  Aviso,
  Botao,
  Campo,
  Esqueleto,
  Etiqueta,
  Vazio,
  cx,
} from '../../componentes/ui';
import { numero, tempoRelativo } from '../../util/formato';
import css from './Suporte.module.css';

/** Conversa aberta: atualiza rápido, para a resposta aparecer sozinha. */
const INTERVALO_CONVERSA_MS = 8_000;
const INTERVALO_LISTA_MS = 30_000;

/**
 * Suporte do cliente: lista de conversas e o fio da conversa escolhida.
 *
 * A conversa escolhida vive na URL (`?c=`), que é o link do e-mail de aviso —
 * quem clica cai direto nela.
 */
export function Suporte() {
  const [params, setParams] = useSearchParams();
  const selecionada = params.get('c');
  const [lista, setLista] = useState<ConversaResumo[] | null>(null);
  const [conversa, setConversa] = useState<Conversa | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregarLista = useCallback(async () => {
    try {
      setLista(await api<ConversaResumo[]>('/suporte/conversas'));
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Falha ao carregar o suporte.',
      );
    }
  }, []);

  useEffect(() => {
    void carregarLista();
    const t = setInterval(() => void carregarLista(), INTERVALO_LISTA_MS);
    return () => clearInterval(t);
  }, [carregarLista]);

  useEffect(() => {
    setConversa(null);
    if (!selecionada) return;
    let vivo = true;
    const buscar = () =>
      api<Conversa>(`/suporte/conversas/${selecionada}`)
        .then((c) => vivo && setConversa(c))
        .catch(() => undefined);
    void buscar();
    const t = setInterval(() => void buscar(), INTERVALO_CONVERSA_MS);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [selecionada]);

  // Abrir a conversa marca como lida: a lista precisa refletir isso já.
  useEffect(() => {
    if (conversa) {
      setLista(
        (l) =>
          l?.map((c) => (c.id === conversa.id ? { ...c, naoLidas: 0 } : c)) ??
          l,
      );
    }
  }, [conversa]);

  function abrir(id: string | null) {
    setParams(id ? { c: id } : {});
  }

  return (
    <Pagina>
      <TituloPagina
        titulo="Suporte"
        descricao="Fale com a gente por aqui. Avisamos por e-mail quando houver resposta."
      />

      {erro && <Aviso>{erro}</Aviso>}

      {/* No celular cabe um painel por vez: com conversa aberta, some a lista
          (o "voltar" do cabeçalho do fio a traz de volta). */}
      <div className={cx(css.mensageiro, selecionada && css.comConversa)}>
        <aside className={css.lista} aria-label="Suas conversas">
          <header className={css.listaCabecalho}>
            <h2 className={css.listaTitulo}>Conversas</h2>
            <Botao
              aparencia={selecionada ? 'secundario' : 'discreto'}
              pequeno
              icone={<MessageSquarePlus />}
              onClick={() => abrir(null)}
              aria-pressed={!selecionada}
            >
              Nova
            </Botao>
          </header>

          {!lista ? (
            <div className={css.listaCarregando}>
              <Esqueleto altura={44} />
              <Esqueleto altura={44} />
              <Esqueleto altura={44} />
            </div>
          ) : lista.length === 0 ? (
            <p className={css.listaVazia}>
              Nenhuma conversa ainda. Escreva ao lado e ela aparece aqui.
            </p>
          ) : (
            <ListaConversas itens={lista} selecionada={selecionada} aoAbrir={abrir} />
          )}
        </aside>

        <section className={css.painel}>
          {!selecionada ? (
            <NovaConversa
              aoCriar={(c) => {
                void carregarLista();
                setConversa(c);
                abrir(c.id);
              }}
            />
          ) : !conversa ? (
            <div className={css.painelCarregando}>
              <Esqueleto altura={20} largura="40%" />
              <Esqueleto altura={56} largura="70%" />
              <Esqueleto altura={56} largura="55%" />
            </div>
          ) : (
            <>
              <header className={css.fioCabecalho}>
                <Botao
                  aparencia="discreto"
                  pequeno
                  soIcone
                  icone={<ArrowLeft />}
                  aria-label="Voltar às conversas"
                  className={css.voltar}
                  onClick={() => abrir(null)}
                />
                <div className={css.fioTitulo}>
                  <h2>{conversa.assunto}</h2>
                  <span>Aberta {tempoRelativo(conversa.createdAt)}</span>
                </div>
                {conversa.status === 'FECHADA' ? (
                  <Etiqueta tom="neutro">Encerrada</Etiqueta>
                ) : (
                  <Etiqueta tom="ok">Aberta</Etiqueta>
                )}
              </header>
              <div className={css.fio}>
                <Chat
                  preencher
                  mensagens={conversa.mensagens}
                  euSou="CLIENTE"
                  nomeOutroLado="Suporte"
                  aviso={
                    conversa.status === 'FECHADA'
                      ? 'Esta conversa foi encerrada. Se escrever de novo, ela é reaberta.'
                      : undefined
                  }
                  aoEnviar={async (texto) => {
                    setConversa(
                      await api<Conversa>(
                        `/suporte/conversas/${conversa.id}/mensagens`,
                        { metodo: 'POST', corpo: { texto } },
                      ),
                    );
                    void carregarLista();
                  }}
                />
              </div>
            </>
          )}
        </section>
      </div>
    </Pagina>
  );
}

function ListaConversas({
  itens,
  selecionada,
  aoAbrir,
}: {
  itens: ConversaResumo[];
  selecionada: string | null;
  aoAbrir: (id: string) => void;
}) {
  return (
    <ul className={css.conversas}>
      {itens.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            onClick={() => aoAbrir(c.id)}
            aria-current={c.id === selecionada ? 'true' : undefined}
            className={cx(css.conversa, c.naoLidas > 0 && css.naoLida)}
          >
            <span className={css.conversaIcone} aria-hidden="true">
              <MessageSquare />
            </span>
            <span className={css.conversaTexto}>
              <span className={css.conversaLinha}>
                <span className={css.conversaAssunto}>{c.assunto}</span>
                <span className={css.conversaQuando}>{tempoRelativo(c.ultimaMensagemEm)}</span>
              </span>
              <span className={css.conversaLinha}>
                <span className={css.conversaStatus}>
                  {c.status === 'FECHADA' ? 'Encerrada' : 'Aberta'}
                </span>
                {c.naoLidas > 0 && (
                  <span
                    className={css.contador}
                    aria-label={`${numero(c.naoLidas)} ${c.naoLidas === 1 ? 'mensagem nova' : 'mensagens novas'}`}
                  >
                    {numero(c.naoLidas)}
                  </span>
                )}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function NovaConversa({ aoCriar }: { aoCriar: (c: Conversa) => void }) {
  const [assunto, setAssunto] = useState('');
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      aoCriar(
        await api<Conversa>('/suporte/conversas', {
          metodo: 'POST',
          corpo: { assunto: assunto.trim(), texto: texto.trim() },
        }),
      );
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Falha ao enviar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={(e) => void enviar(e)} className={css.nova}>
      <Vazio
        icone={<MessageSquarePlus />}
        titulo="Nova conversa"
        descricao="Conte o que aconteceu. Quando o suporte responder, você recebe um aviso no e-mail da conta e a conversa aparece na lista marcada como nova."
      />
      <div className={css.novaCampos}>
        <Campo
          rotulo="Assunto"
          value={assunto}
          maxLength={120}
          placeholder="Ex.: lote parado, dúvida sobre recarga"
          onChange={(e) => setAssunto(e.target.value)}
        />
        <AreaTexto
          rotulo="Mensagem"
          mono={false}
          value={texto}
          maxLength={4000}
          rows={6}
          placeholder="Se for sobre um lote, cole o número dele."
          onChange={(e) => setTexto(e.target.value)}
        />
        {erro && <Aviso>{erro}</Aviso>}
        <div>
          <Botao
            type="submit"
            icone={<Send />}
            carregando={enviando}
            disabled={assunto.trim().length < 3 || !texto.trim()}
          >
            Enviar
          </Botao>
        </div>
      </div>
    </form>
  );
}
